import { validationConstants } from "constants/formValidation";
import { orderStatusConstants, orderTypeConstants } from "constants/order";
import { distributionLogicOptions } from "constants/paymentPlanConstants";
import { UserRole } from "constants/userRole";
import { useUserWithAccount } from "context/UserAccountProvider";
import { parseDate } from "helpers/date";
import { parsePrice, centsToDollars, dollarsToCents } from "helpers/price";
import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import { handleChangeDateWithMask } from "pages/CreateOrder/utils/mask";
import { Dropdown } from "primereact/dropdown";
import {
  InputNumber,
  InputNumberValueChangeParams,
} from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { useMemo, useCallback, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { IOrder } from "types/Order/Order";
import { IPaymentPlan } from "types/Payment";

import {
  validateNextInstallmentDate,
  validateMinDownPaymentFlat,
} from "../helpers/helper";
import { IPaymentDetailsEdit } from "../PaymentDetails";
import { PaymentPlanDetails } from "./PaymentPlanDetails";
import { RemainingAmountEditTable } from "./RemainingAmountEditTable";

export function PaymentPlanSection({
  editing,
  editable,
  orderStatus,
  totalPatientPortion,
  order,
  orderType,
  paymentPlanBreakDown,
  paymentPlan,
  setPaymentPlanFieldsValid,
  showPaymentPlanEnableButton,
  paymentPlanRemaining,
  differenceByProvider,
  setDifferenceByProvider,
  valuesByProvider,
  setValuesByProvider,
  initialRemainingInstallmentsCount,
}: {
  editing: boolean;
  editable: boolean;
  orderStatus: string;
  totalPatientPortion: number | null;
  order?: IOrder;
  orderType: string;
  paymentPlanBreakDown?: any;
  paymentPlan?: IPaymentPlan;
  setPaymentPlanFieldsValid: (isValid: boolean) => void;
  showPaymentPlanEnableButton: boolean;
  paymentPlanRemaining?: Array<{
    providerId: string;
    remainingAmountInCents: number;
    providerPriority?: number | null;
  }>;
  differenceByProvider: { [key: string]: number };
  setDifferenceByProvider: (differenceByProvider: {
    [key: string]: number;
  }) => void;
  valuesByProvider: { [key: string]: number };
  setValuesByProvider: (valuesByProvider: { [key: string]: number }) => void;
  initialRemainingInstallmentsCount: number | null;
}) {
  const {
    control,
    getValues,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<IPaymentDetailsEdit>();
  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();
  const { userInfo } = useUserWithAccount();
  const minDownPaymentType = watch("paymentPlanMinDownPaymentType");
  const minDownPaymenFlat = watch("paymentPlanMinDownPaymentFlat");
  const remainingInstallmentsField = watch("paymentPlanDuration");
  const remainingAmountField = watch("paymentPlanRemainingAmount");
  const nextInstallmentDateField = watch("nextInstallmentDate");
  const minDownPaymentPercent = watch("paymentPlanMinDownPaymentPercent");

  // State to persist the attention message
  const [persistentRecommendation, setPersistentRecommendation] = useState<
    string | null
  >(null);

  // Track the last remaining amount that triggered auto-adjustment
  const [lastAdjustedAmount, setLastAdjustedAmount] = useState<number | null>(
    null
  );

  // Track previous installments to detect manual changes
  const [prevInstallments, setPrevInstallments] = useState<number | null>(
    remainingInstallmentsField
  );

  const [showMinPaymentWarning, setShowMinPaymentWarning] = useState(false);

  // Helper function to calculate payment breakdown data
  const calculatePaymentBreakdown = useCallback(
    (
      paymentPlanBreakDown: any,
      hasClearPatientFee: boolean,
      paymentPlanRemaining?: any[]
    ) => {
      if (!paymentPlanBreakDown?.payments?.length) {
        return { providers: [], accurateTotal: null };
      }

      const paymentsWithoutInitialDeposit =
        paymentPlanBreakDown.payments.filter((p: any) => !!p.installmentInDb);
      const paymentsStillNotPaid = paymentsWithoutInitialDeposit.filter(
        (p: any) => !p.installmentInDb.paymentId
      );

      const providerMap = new Map();
      let totalWithClearPatientFee = 0;

      // Build the provider map from breakdown data to get metadata
      // Use ALL payments (including initial deposit) to ensure we have provider metadata
      paymentPlanBreakDown.payments.forEach((payment: any) => {
        payment.providerBreakdown.forEach((provider: any) => {
          const { providerId, providerName, providerPriority, cptCode } =
            provider;
          if (!providerMap.has(providerId)) {
            providerMap.set(providerId, {
              providerId,
              providerName,
              providerPriority,
              cptCode,
              totalExpectedTransferAmount: 0,
            });
          }
        });
      });

      // Calculate accurate total from unpaid payments only (for clear patient fee calculation)
      paymentsStillNotPaid.forEach((payment: any) => {
        payment.providerBreakdown.forEach((provider: any) => {
          // For accurate total calculation (only when clear patient fee exists)
          if (hasClearPatientFee) {
            totalWithClearPatientFee +=
              provider.expectedTransferAmount +
              provider.clearFeeAmountInCents +
              (provider.clearPatientFeeAmountInCents || 0);
          }
        });
      });

      // Set remaining amounts from backend data
      if (paymentPlanRemaining?.length) {
        paymentPlanRemaining.forEach((remaining) => {
          if (providerMap.has(remaining.providerId)) {
            providerMap.get(remaining.providerId).totalExpectedTransferAmount =
              remaining.remainingAmountInCents;
          }
        });
      }

      return {
        providers: Array.from(providerMap.values()),
        accurateTotal: hasClearPatientFee ? totalWithClearPatientFee : null,
      };
    },
    []
  );

  // Calculate both provider breakdown and accurate total in one helper function
  const paymentBreakdownResult = useMemo(() => {
    const hasClearPatientFee =
      order?.clearPatientFeeValueInPercentage &&
      order.clearPatientFeeValueInPercentage > 0;
    return calculatePaymentBreakdown(
      paymentPlanBreakDown,
      !!hasClearPatientFee,
      paymentPlanRemaining
    );
  }, [
    paymentPlanBreakDown,
    order?.clearPatientFeeValueInPercentage,
    calculatePaymentBreakdown,
    paymentPlanRemaining,
  ]);

  const remainingAmountBreakdown = paymentBreakdownResult.providers;
  const accurateRemainingTotal = paymentBreakdownResult.accurateTotal;

  const minDownPaymentEnabled = useMemo(() => {
    if (orderStatus !== orderStatusConstants.SENT_TO_PATIENT || !editing) {
      return false;
    }
    return true;
  }, [orderStatus, editing]);

  const showMinDownPaymentFields = useMemo(() => {
    if (!editing) {
      return true;
    }
    return minDownPaymentEnabled;
  }, [minDownPaymentEnabled, editing]);

  const showPercentageField = useMemo(() => {
    if (!minDownPaymentType) {
      return false;
    }
    if (minDownPaymentType === "Percentage") {
      return true;
    }
    return false;
  }, [minDownPaymentType]);

  const showFlatAmountField = useMemo(() => {
    if (!minDownPaymentType) {
      return false;
    }
    if (minDownPaymentType === "Flat amount") {
      return true;
    }
    return false;
  }, [minDownPaymentType]);

  // Create validation function that uses the helper with current state
  const validateMinDownPaymentFlatWithState = useCallback(
    (value: number | null | undefined) => {
      return validateMinDownPaymentFlat(
        value,
        totalPatientPortion,
        order?.clearPatientFeeValueInPercentage,
        order?.status
      );
    },
    [totalPatientPortion, order?.clearPatientFeeValueInPercentage]
  );

  // Calculate display value for minimum down payment flat without clear patient fee, when it exists
  const minDownPaymentFlatDisplayValue = useMemo(() => {
    return minDownPaymenFlat &&
      order?.clearPatientFeeValueInPercentage &&
      orderType === orderTypeConstants.GFE
      ? minDownPaymenFlat / (1 + order.clearPatientFeeValueInPercentage / 100)
      : minDownPaymenFlat;
  }, [minDownPaymenFlat, order?.clearPatientFeeValueInPercentage, orderType]);

  const showRemainingAmountField = useMemo(() => {
    if (orderType === orderTypeConstants.PATIENT_RESPONSIBILITY) {
      return true;
    }
    return !editing;
  }, [editing, orderStatus]);

  const remainingAmountDisabled = useMemo(() => {
    if (orderStatus === orderStatusConstants.SENT_TO_PATIENT) {
      return true;
    }
    return !editing;
  }, [orderStatus, editing]);

  const showNextInstallmentDate = useMemo(
    () =>
      orderStatus === orderStatusConstants.PARTIALLY_PAID ||
      (editing &&
        orderStatus === orderStatusConstants.PAID &&
        !!paymentPlanBreakDown?.payments?.length),
    [orderStatus, paymentPlan, editing, paymentPlanBreakDown]
  );

  const showRemainingAmountsSection = useMemo(() => {
    return (
      editing &&
      (orderStatus === orderStatusConstants.PARTIALLY_PAID ||
        orderStatus === orderStatusConstants.PAID) &&
      orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY
    );
  }, [editing, orderStatus, orderType]);

  const isPaymentPlanRemainingAmountChanged = useMemo(() => {
    return (
      dollarsToCents(remainingAmountField || 0) !==
      paymentPlan?.remainingAmountInCents
    );
  }, [remainingAmountField, paymentPlan?.remainingAmountInCents]);

  const rearrangeFieldsOrder = useMemo(() => {
    return orderStatus === orderStatusConstants.SENT_TO_PATIENT && editing;
  }, [orderStatus, editing]);

  // Calculation function for monthly payment and recommendations
  const calculatePaymentInfo = (
    currentAmount: number,
    initialAmount: number,
    currentInstallments: number,
    hasChanges: boolean
  ) => {
    if (!currentInstallments || currentInstallments <= 0) {
      return {
        monthlyPaymentInfo: null,
        recommendationInfo: null,
        adjustedInstallments: null,
      };
    }

    const currentMonthlyPayment = currentAmount / currentInstallments;

    const currentMonthlyText = parsePrice(
      centsToDollars(currentMonthlyPayment)
    );

    // Only show "previously" if we have valid initial data
    const monthlyPaymentInfo =
      hasChanges &&
      initialRemainingInstallmentsCount &&
      initialRemainingInstallmentsCount > 0
        ? (() => {
            const previousMonthlyPayment =
              initialAmount / initialRemainingInstallmentsCount;
            const previousMonthlyText = parsePrice(
              centsToDollars(previousMonthlyPayment)
            );
            return `${currentMonthlyText} (previously ${previousMonthlyText})`;
          })()
        : `${currentMonthlyText}`;

    // Calculate recommendation
    let recommendationInfo = null;
    if (
      hasChanges &&
      initialRemainingInstallmentsCount &&
      initialRemainingInstallmentsCount > 0
    ) {
      // Use the ORIGINAL installments count for the initial monthly payment calculation
      const initialPreviousMonthlyPayment =
        initialAmount / initialRemainingInstallmentsCount;

      // Calculate the best possible duration to get close to initial monthly payment
      const idealMonths = currentAmount / initialPreviousMonthlyPayment;
      let bestPossibleDurationCount = Math.max(1, Math.round(idealMonths));
      let recommendedMonthlyPayment = currentAmount / bestPossibleDurationCount;

      // Check if recommended monthly payment exceeds 6% above original
      const maxAllowedMonthlyPayment = initialPreviousMonthlyPayment * 1.06; // 6% above original
      if (recommendedMonthlyPayment > maxAllowedMonthlyPayment) {
        // Use the 2nd closest option (more months, lower payment)
        bestPossibleDurationCount += 1;
        recommendedMonthlyPayment = currentAmount / bestPossibleDurationCount;
      }

      // Check if the difference is significant (more than 7 cents)
      const monthlyPaymentDifference = Math.abs(
        currentMonthlyPayment - initialPreviousMonthlyPayment
      );
      const recommendedDifference = Math.abs(
        recommendedMonthlyPayment - initialPreviousMonthlyPayment
      );

      // Only show recommendation if:
      // 1. The current difference is more than $0.07 (7 cents)
      // 2. The recommended option is better than current
      // 3. The recommended duration is different from current
      if (
        monthlyPaymentDifference > 7 &&
        recommendedDifference < monthlyPaymentDifference &&
        bestPossibleDurationCount !== currentInstallments
      ) {
        const initialPreviousMonthlyText = parsePrice(
          centsToDollars(initialPreviousMonthlyPayment)
        );

        recommendationInfo = `The number of months has been automatically adjusted to ${bestPossibleDurationCount} to keep the monthly payment close to ${initialPreviousMonthlyText}.`;
        return {
          monthlyPaymentInfo,
          recommendationInfo,
          adjustedInstallments: bestPossibleDurationCount,
        };
      }
    }

    return {
      monthlyPaymentInfo,
      recommendationInfo,
      adjustedInstallments: null,
    };
  };

  useEffect(() => {
    const validateFields = async () => {
      let isValidFlatAmount = false;
      let isValidNextInstallmentDate = false;
      let isValidRemainingAmount = false;
      const isValidRemainingInstallments =
        remainingInstallmentsField !== null &&
        remainingInstallmentsField !== undefined;
      const isPercentageType = minDownPaymentType === "Percentage";
      const isFlatValid = await trigger("paymentPlanMinDownPaymentFlat");
      const isPercentageValid = await trigger(
        "paymentPlanMinDownPaymentPercent"
      );
      if (isPercentageType) {
        isValidFlatAmount = !!isPercentageValid;
      } else {
        isValidFlatAmount = !!isFlatValid;
      }
      if (!showNextInstallmentDate) {
        isValidNextInstallmentDate = true;
      } else {
        const validNextInstallmentDate = validateNextInstallmentDate(
          nextInstallmentDateField,
          "future"
        );
        isValidNextInstallmentDate =
          typeof validNextInstallmentDate !== "string";
      }
      if (orderStatus === orderStatusConstants.SENT_TO_PATIENT) {
        isValidRemainingAmount = true;
      } else {
        isValidRemainingAmount =
          remainingAmountField !== null && remainingAmountField !== undefined;
      }
      setPaymentPlanFieldsValid(
        isValidFlatAmount &&
          isValidNextInstallmentDate &&
          isValidRemainingAmount &&
          isValidRemainingInstallments
      );
    };
    validateFields();
  }, [
    minDownPaymenFlat,
    minDownPaymentPercent,
    trigger,
    minDownPaymentType,
    nextInstallmentDateField,
    remainingInstallmentsField,
    remainingAmountField,
    totalPatientPortion,
  ]);

  // Monthly payment calculation for Patient Responsibility orders
  const monthlyPaymentInfo = useMemo(() => {
    if (
      orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY ||
      !editing ||
      !remainingInstallmentsField ||
      remainingInstallmentsField <= 0 ||
      orderStatus !== orderStatusConstants.PARTIALLY_PAID
    ) {
      return null;
    }

    const currentRemainingAmount = remainingAmountField
      ? dollarsToCents(remainingAmountField)
      : 0;
    const initialRemainingAmount = paymentPlan?.remainingAmountInCents || 0;
    const hasChanges = currentRemainingAmount !== initialRemainingAmount;

    const { monthlyPaymentInfo } = calculatePaymentInfo(
      currentRemainingAmount,
      initialRemainingAmount,
      remainingInstallmentsField,
      hasChanges
    );

    return monthlyPaymentInfo;
  }, [
    orderType,
    editing,
    remainingInstallmentsField,
    remainingAmountField,
    paymentPlan?.remainingAmountInCents,
    initialRemainingInstallmentsCount,
  ]);

  const onNextInstallmentDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void
  ) => {
    const newValue = e.target.value;
    let currentValue = "";
    if (paymentPlan?.installments?.length) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset today's time to midnight
      const sortedInstallments = [...paymentPlan.installments]
        .filter((installment) => {
          const installmentDate = new Date(installment.dateDue);
          installmentDate.setHours(0, 0, 0, 0); // Reset installment's time to midnight
          return installmentDate >= today; // Compare only the date parts
        })
        .sort(
          (a, b) =>
            new Date(a.dateDue).getTime() - new Date(b.dateDue).getTime()
        ); // Sort by dateDue

      // first installment with a null paymentId
      const firstNullPaymentIdInstallment = sortedInstallments.find(
        (installment) => !installment.paymentId
      );
      currentValue = firstNullPaymentIdInstallment?.dateDue
        ? parseDate(firstNullPaymentIdInstallment.dateDue, false, true)
        : "-";
    }

    setValue("nextInstallmentDateChanged", newValue !== currentValue);
    handleChangeDateWithMask(e, onChange);
  };

  // Show only persistent recommendation after auto-adjustment has happened
  // (Don't fall back to calculated one, or message won't hide when user edits)
  const displayRecommendationInfo = persistentRecommendation;

  // Recommendation calculation for Patient Responsibility orders
  const recommendationData = useMemo(() => {
    if (
      orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY ||
      !editing ||
      !remainingInstallmentsField ||
      remainingInstallmentsField <= 0 ||
      !initialRemainingInstallmentsCount ||
      initialRemainingInstallmentsCount <= 0 ||
      orderStatus !== orderStatusConstants.PARTIALLY_PAID
    ) {
      return { recommendationInfo: null, adjustedInstallments: null };
    }

    const currentRemainingAmount = remainingAmountField
      ? dollarsToCents(remainingAmountField)
      : 0;
    const initialRemainingAmount = paymentPlan?.remainingAmountInCents || 0;
    const hasChanges = currentRemainingAmount !== initialRemainingAmount;

    if (!hasChanges) {
      return { recommendationInfo: null, adjustedInstallments: null };
    }

    const { recommendationInfo, adjustedInstallments } = calculatePaymentInfo(
      currentRemainingAmount,
      initialRemainingAmount,
      initialRemainingInstallmentsCount,
      hasChanges
    );

    return { recommendationInfo, adjustedInstallments };
  }, [
    orderType,
    editing,
    remainingInstallmentsField,
    remainingAmountField,
    paymentPlan?.remainingAmountInCents,
    initialRemainingInstallmentsCount,
    orderStatus,
  ]);

  const { recommendationInfo, adjustedInstallments } = recommendationData;

  const isPaymentPlanActivated = useMemo(() => {
    return paymentPlan?.installments?.length;
  }, [paymentPlan?.installments?.length]);

  const remainingPaymentsFieldsLogicActive = useMemo(() => {
    return (
      !isPaymentPlanActivated &&
      [
        orderStatusConstants.SENT_TO_PATIENT,
        orderStatusConstants.NO_BALANCE_DUE,
      ].includes(orderStatus)
    );
  }, [isPaymentPlanActivated, orderStatus]);

  const numberOfPaymentsLabel = useMemo(() => {
    if (rearrangeFieldsOrder) {
      return "Number of payments";
    }
    return remainingPaymentsFieldsLogicActive
      ? "Remaining Payments"
      : "Remaining Installments";
  }, [rearrangeFieldsOrder, remainingPaymentsFieldsLogicActive]);

  const onNumberOfPaymentsChange = (e: InputNumberValueChangeParams) => {
    const newValue = e.value;
    if (newValue) {
      // Only set the type if it's not already "Flat amount" to avoid triggering
      // the useEffect in PaymentDetails that resets the flat value
      if (minDownPaymentType !== "Flat amount") {
        setValue("paymentPlanMinDownPaymentType", "Flat amount");
        setValue("paymentPlanMinDownPaymentPercent", null);
      }
      const downPaymentValue = centsToDollars(
        (totalPatientPortion || 0) / newValue
      );
      setValue("paymentPlanMinDownPaymentFlat", downPaymentValue);
    }
  };

  const getMinDownPaymentMessage = (value: number) => {
    const remainingAmount = centsToDollars(totalPatientPortion || 0) - value;
    const monthlyPayment = remainingInstallmentsField
      ? Math.floor((remainingAmount / remainingInstallmentsField) * 100) / 100
      : 0;
    return Math.abs(monthlyPayment - value) < 0.1
      ? `Same as monthly payment (default)`
      : "Custom amount";
  };

  // Auto-update the Remaining Installments field when remaining amount changes
  useEffect(() => {
    const currentAmount = remainingAmountField
      ? dollarsToCents(remainingAmountField)
      : 0;
    const amountChanged = currentAmount !== lastAdjustedAmount;

    if (
      recommendationInfo &&
      adjustedInstallments &&
      editing &&
      amountChanged
    ) {
      // Save the recommendation message to persist it
      setPersistentRecommendation(recommendationInfo);

      // Use the adjusted installments number directly
      if (adjustedInstallments !== remainingInstallmentsField) {
        // Update tracking BEFORE setValue to prevent the "hide" useEffect from triggering
        setLastAdjustedAmount(currentAmount);
        setPrevInstallments(adjustedInstallments);
        setValue("paymentPlanDuration", adjustedInstallments);
      }
    }
  }, [
    recommendationInfo,
    adjustedInstallments,
    editing,
    remainingAmountField,
    setValue,
    lastAdjustedAmount,
  ]);

  // Hide message when user manually changes installments
  useEffect(() => {
    if (
      prevInstallments !== null &&
      remainingInstallmentsField !== prevInstallments
    ) {
      // Check if this is from our auto-adjustment by comparing with adjustedInstallments
      // If the field matches adjustedInstallments, it's our auto-update, not a manual change
      if (
        adjustedInstallments &&
        remainingInstallmentsField === adjustedInstallments
      ) {
        // This is our auto-adjustment, just update prevInstallments
        setPrevInstallments(remainingInstallmentsField);
      } else {
        // User manually changed installments - hide the message
        setPersistentRecommendation(null);
        setPrevInstallments(remainingInstallmentsField);
      }
    }
  }, [remainingInstallmentsField, prevInstallments, adjustedInstallments]);

  // Reset persistent recommendation and flags when editing is toggled off
  useEffect(() => {
    if (!editing) {
      setPersistentRecommendation(null);
      setLastAdjustedAmount(null);
      setPrevInstallments(remainingInstallmentsField);
    }
  }, [editing, remainingInstallmentsField]);

  return (
    <div className="flex flex-column gap-2">
      <div className="formgrid grid w-100">
        {showMinDownPaymentFields && (
          <div className="field flex flex-column col-12 md:col-3 lg:col-2 xl:col-1">
            <Label
              htmlFor="paymentPlanMinDownPaymentType"
              data-testid="paymentPlanMinDownPaymentType"
            >
              Type
            </Label>
            <Controller
              name="paymentPlanMinDownPaymentType"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  id="paymentPlanMinDownPaymentType"
                  data-testid="minDownPaymentType_PaymentPlan"
                  value={value}
                  disabled={!minDownPaymentEnabled}
                  onChange={onChange}
                  options={[
                    { label: "%", value: "Percentage" },
                    { label: "$", value: "Flat amount" },
                  ]}
                  placeholder="Select minimum down payment type"
                  className="w-full"
                />
              )}
            />
            {minDownPaymentType === "Percentage" &&
              rearrangeFieldsOrder &&
              minDownPaymentPercent && (
                <span className="text-sm mt-1 text-gray-600">
                  ={" "}
                  {parsePrice(
                    (centsToDollars(totalPatientPortion || 0) *
                      minDownPaymentPercent) /
                      100
                  )}
                </span>
              )}
          </div>
        )}
        {showPercentageField && showMinDownPaymentFields && (
          <div className="field flex flex-column col-12 md:col-4">
            <Label htmlFor="paymentPlanMinDownPaymentPercent">
              Minimum Down Payment
            </Label>
            <Controller
              data-testid="paymentPlanMinDownPaymentPercent"
              name="paymentPlanMinDownPaymentPercent"
              control={control}
              rules={{
                required:
                  validationConstants.PAYMENT_PLAN.MIN_DOWN_PAYMENT_PERCENT,
              }}
              render={({ field: { value, onChange, onBlur } }) => (
                <>
                  <InputNumber
                    disabled={!minDownPaymentEnabled}
                    value={value}
                    min={5}
                    onValueChange={onChange}
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "paymentPlanMinDownPaymentPercent",
                        trigger,
                      })
                    }
                    suffix="%"
                    inputId="paymentPlanMinDownPaymentPercent"
                    data-testid="paymentPlanMinDownPaymentPercent"
                  />
                  {getFormErrorMessage(
                    "paymentPlanMinDownPaymentPercent",
                    errors
                  )}
                </>
              )}
            />
          </div>
        )}
        {showFlatAmountField && showMinDownPaymentFields && (
          <div className="field flex flex-column col-12 md:col-3">
            <Label
              htmlFor="paymentPlanMinDownPaymentFlat"
              data-testid="paymentPlanMinDownPaymentFlat"
            >
              Minimum first payment flat
            </Label>

            <Controller
              name="paymentPlanMinDownPaymentFlat"
              control={control}
              rules={{
                required:
                  validationConstants.PAYMENT_PLAN.MIN_DOWN_PAYMENT_FLAT,
                validate: validateMinDownPaymentFlatWithState,
              }}
              render={({ field: { onChange, value, onBlur } }) => (
                <>
                  <InputNumber
                    value={minDownPaymentFlatDisplayValue}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                    disabled={!minDownPaymentEnabled}
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "paymentPlanMinDownPaymentFlat",
                        trigger,
                      })
                    }
                    inputId="paymentPlanMinDownPaymentFlat"
                    data-testid="minDownPaymentFlat_PaymentPlan"
                    onValueChange={(e) => {
                      // When user changes value, calculate total with fees for storage
                      const newValue = e.value;
                      const valueToStore =
                        newValue &&
                        order?.clearPatientFeeValueInPercentage &&
                        orderType === orderTypeConstants.GFE
                          ? newValue *
                            (1 + order.clearPatientFeeValueInPercentage / 100)
                          : newValue;
                      onChange(valueToStore);
                    }}
                    min={10}
                    aria-autocomplete="none"
                  />
                  <div>
                    {value &&
                      order?.clearPatientFeeValueInPercentage &&
                      orderType === orderTypeConstants.GFE && (
                        <span className="text-sm mt-1 text-primary">
                          with fees: {parsePrice(value)}
                        </span>
                      )}{" "}
                    {value !== null &&
                      value !== undefined &&
                      rearrangeFieldsOrder && (
                        <span className="text-xs mts-1 text-gray-600">
                          {getMinDownPaymentMessage(value)}
                        </span>
                      )}
                  </div>
                  {getFormErrorMessage("paymentPlanMinDownPaymentFlat", errors)}
                </>
              )}
            />
          </div>
        )}
        {editable && (
          <div
            className={`field flex flex-column col-12 md:col-4 ${
              rearrangeFieldsOrder && "lg:col-4 xl:col-3"
            }`}
            style={{
              order: rearrangeFieldsOrder ? -1 : undefined,
            }}
          >
            <Label htmlFor="paymentPlanDurationFull">
              {numberOfPaymentsLabel}
            </Label>
            <Controller
              data-testid="paymentPlanDurationFull"
              name="paymentPlanDurationFull"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  <InputNumber
                    disabled={!editing}
                    id="paymentPlanDurationFull"
                    aria-labelledby="paymentPlanDurationFull"
                    value={value}
                    onValueChange={(e) => {
                      if (e.value === null || e.value === undefined) {
                        setValue("paymentPlanDuration", null);
                      }
                      if (e.value === 0) {
                        setValue("paymentPlanDuration", 0);
                      }
                      if (e.value) {
                        setValue(
                          "paymentPlanDuration",
                          !remainingPaymentsFieldsLogicActive
                            ? e.value
                            : e.value - 1
                        );
                      }
                      onChange(e.value);
                      if (
                        rearrangeFieldsOrder &&
                        remainingPaymentsFieldsLogicActive
                      ) {
                        onNumberOfPaymentsChange(e);
                      }
                    }}
                    onChange={(e) =>
                      e.value !== null &&
                      e.value !== undefined &&
                      remainingPaymentsFieldsLogicActive &&
                      setShowMinPaymentWarning(e.value < 2)
                    }
                    min={!remainingPaymentsFieldsLogicActive ? 0 : 2}
                    max={!remainingPaymentsFieldsLogicActive ? 50 : 51}
                  />
                  {showMinPaymentWarning &&
                    remainingPaymentsFieldsLogicActive && (
                      <p className="text-sm mt-1 text-orange-500">
                        Minimum: 2 payments (today + next installment)
                      </p>
                    )}
                </>
              )}
            />
          </div>
        )}
        {showRemainingAmountField && (
          <div
            className="field flex flex-column col-12 md:col-4"
            style={{
              order:
                orderType === orderTypeConstants.PATIENT_RESPONSIBILITY &&
                orderStatus === orderStatusConstants.SENT_TO_PATIENT
                  ? 2
                  : undefined,
            }}
          >
            <Label htmlFor="paymentPlanRemainingAmount">Remaining Amount</Label>
            <Controller
              data-testid="paymentPlanRemainingAmount"
              name="paymentPlanRemainingAmount"
              control={control}
              render={({ field: { value, onChange } }) =>
                remainingAmountDisabled && !value ? (
                  <InputText value="-" disabled />
                ) : (
                  <InputNumber
                    disabled={remainingAmountDisabled}
                    value={value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                    inputId="paymentPlanRemainingAmount"
                    data-testid="paymentPlanRemainingAmount"
                    onValueChange={onChange}
                    min={0}
                  />
                )
              }
            />
          </div>
        )}
        {showNextInstallmentDate && (
          <div className="field flex flex-column col-12 md:col-4">
            <Label htmlFor="nextInstallmentDate">Next Installment Date</Label>
            <Controller
              data-testid="nextInstallmentDate"
              name="nextInstallmentDate"
              control={control}
              rules={{
                validate: (date) => validateNextInstallmentDate(date, "future"),
                required:
                  validationConstants.PAYMENT_PLAN.NEXT_INSTALLMENT_DATE,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <InputText
                    disabled={!editing}
                    id="nextInstallmentDate"
                    value={value || ""}
                    placeholder="99/99/9999"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "nextInstallmentDate",
                        trigger,
                      })
                    }
                    onChange={(e) => onNextInstallmentDateChange(e, onChange)}
                  />

                  {getFormErrorMessage("nextInstallmentDate", errors)}
                </>
              )}
            />
          </div>
        )}
        {orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY &&
          userInfo?.scope === UserRole.SUPER_ADMIN && (
            <div
              className="field flex-column col-12 md:col-3"
              style={{ order: rearrangeFieldsOrder ? 1 : undefined }}
            >
              <Label
                htmlFor="providerDistributionLogic"
                data-testid="providerDistributionLogic"
              >
                Provider Distribution Logic
              </Label>
              <Controller
                name="providerDistributionLogic"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    id="providerDistributionLogic"
                    data-testid="providerDistributionLogic_PaymentPlan"
                    value={value}
                    onChange={onChange}
                    disabled={!editing}
                    options={Object.values(distributionLogicOptions)}
                    placeholder="Select provider distribution logic"
                    className="w-full"
                  />
                )}
              />
            </div>
          )}
        {showPaymentPlanEnableButton && (
          <div
            className={`field flex flex-column col-12 md:col-3 ${
              rearrangeFieldsOrder ? "lg:col-4" : "lg:col-2"
            }`}
          >
            <Label
              htmlFor="paymentPlanEnabled"
              data-testid="paymentPlanEnabled"
            >
              Payment Plan Enabled
            </Label>
            <Controller
              name="paymentPlanEnabled"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputSwitch
                  checked={value}
                  onChange={onChange}
                  disabled={!editing}
                />
              )}
            />
          </div>
        )}
        {showRemainingAmountsSection && (
          <RemainingAmountEditTable
            key={`${order?.updatedAt}-${order?.id}`}
            remainingAmountBreakdown={remainingAmountBreakdown}
            differenceByProvider={differenceByProvider}
            setDifferenceByProvider={setDifferenceByProvider}
            setValuesByProvider={setValuesByProvider}
            valuesByProvider={valuesByProvider}
            clearFeePercentage={Number(
              paymentPlanBreakDown?.clearFee?.valueInPercentage
            )}
            clearPatientFeePercentage={order?.clearPatientFeeValueInPercentage}
            isGFE={orderType === orderTypeConstants.GFE}
            initialPaymentPlanRemainingAmount={
              paymentPlan?.remainingAmountInCents || 0
            }
            remainingInstallments={remainingInstallmentsField}
            calculatePaymentInfo={calculatePaymentInfo}
            accurateRemainingTotal={accurateRemainingTotal}
          />
        )}
      </div>
      <div>
        {monthlyPaymentInfo && (
          <>
            <div className="flex w-full bg-gray-100 border-t-1 border-gray-300 py-3 gap-4">
              <div className="w-9 flex align-items-center justify-content-end">
                <p className="font-bold">Remaining amount:</p>
              </div>
              <div className="w-3">
                <p className="font-bold">
                  {parsePrice(remainingAmountField || 0)}{" "}
                  {isPaymentPlanRemainingAmountChanged &&
                    `(previously ${parsePrice(
                      centsToDollars(paymentPlan?.remainingAmountInCents || 0)
                    )})`}
                </p>
              </div>
            </div>
            <div className="flex w-full bg-gray-100 border-y-1 border-gray-300 py-3 gap-4">
              <div className="w-9 flex align-items-center justify-content-end">
                <p className="font-bold">Monthly Payment Amount:</p>
              </div>
              <div className="w-3">
                <p className="font-bold">{monthlyPaymentInfo}</p>
              </div>
            </div>
          </>
        )}
        {displayRecommendationInfo && (
          <div className="flex w-full bg-gray-100 border-bottom-1 border-gray-300 py-3 gap-4">
            <div className="w-9 flex align-items-center justify-content-end gap-2">
              <i className="pi pi-exclamation-triangle text-orange-500 text-base" />
              <p className="text-base font-medium text-orange-500">
                Attention:
              </p>
            </div>
            <div className="w-3">
              <p className="text-sm font-medium text-orange-700">
                {displayRecommendationInfo}
              </p>
            </div>
          </div>
        )}
      </div>
      {[orderStatusConstants.SENT_TO_PATIENT].includes(orderStatus) &&
        editing && (
          <PaymentPlanDetails
            totalPatientPortion={totalPatientPortion}
            order={order}
          />
        )}
    </div>
  );
}
